// SB6-ANA-PROG-01 — dataset-open readiness model.
//
// A DERIVED phase machine. It issues NO new requests: every input is a
// query/hydration/activation signal that the analysis surface already
// owns. The bridge hook (`useAnalysisReadinessBridge`, see
// `../shell/useAnalysisReadinessBridge.ts`) reads those signals and writes
// the derived snapshot into this store on every change; consumers (the
// banner, the statusbar, the grading gate) read it back via selectors.
//
// Why a store and not just a hook? The grading GATE must be enforced at
// the action layer (keyboard listeners in FindingsPanel/DefectsPanel, the
// DefectDrawer save) — code paths that are far from AnalysisFeature and
// must not be prop-drilled. A tiny Zustand store lets any of them read the
// live phase synchronously (`getState`) without threading props through
// half the feature.
//
// Phases (story §Scope):
//   shell          — route mounted, widgets skeleton (always true once the
//                    surface renders).
//   coreData       — the 5 core queries settled (acquisition detail, prefs,
//                    images, findings, defects).
//   filterApplied  — filter hydrated (`dataPointFilterStore.hydrated`) AND
//                    the data-points / facets queries for that selection
//                    settled (reuses PROG-02's apply-state model).
//   dataPointReady — the initial active data point is set + the viewer is
//                    showing it (`imageActivationStore.activeImageId`).
//
// A failed CORE query short-circuits to an `error` phase that names the
// failed step — the banner shows it with a Retry affordance, never an
// infinite spinner.
import { create } from "zustand";

/** The ordered readiness phases. `error` is terminal-until-retry. */
export type ReadinessPhase =
  | "shell"
  | "coreData"
  | "filterApplied"
  | "dataPointReady"
  | "error";

/** Identifies which step failed so the banner can name + retry it. */
export type ReadinessStep =
  | "detail"
  | "prefs"
  | "images"
  | "findings"
  | "defects"
  | "dataPoints";

/** One query's settle/error signal, as the bridge observes it. */
export interface QuerySignal {
  /** True once the query has resolved or errored (not pending). */
  settled: boolean;
  /** True when the query is in an error state. */
  isError: boolean;
}

/** The raw inputs the phase machine derives from. No React in here. */
export interface ReadinessInputs {
  /** Core-data queries (story §"5 parallel core queries"). */
  detail: QuerySignal;
  prefs: QuerySignal;
  images: QuerySignal;
  findings: QuerySignal;
  defects: QuerySignal;
  /** The filtered-view query (data-points). Its error also surfaces. */
  dataPoints: QuerySignal;
  /** `dataPointFilterStore.hydrated`. */
  filterHydrated: boolean;
  /**
   * True while the data-points / facets queries for the current selection
   * are still settling (PROG-02's `useFilterApplyState().applying`). The
   * filter is "applied" once this is false.
   */
  filterApplying: boolean;
  /** `imageActivationStore.activeImageId !== null` — initial DP is set. */
  hasActiveImage: boolean;
  /**
   * True when the dataset has zero images. With no image there is never an
   * active data point; readiness must still resolve (the surface shows the
   * "No images" panel) rather than hang on `dataPointReady` forever.
   */
  datasetEmpty: boolean;
}

export interface ReadinessSnapshot {
  phase: ReadinessPhase;
  /** Per-phase reached booleans (monotonic up to the current phase). */
  shellReady: boolean;
  coreDataReady: boolean;
  filterApplied: boolean;
  dataPointReady: boolean;
  /** Aggregate: the surface is fully ready to grade. */
  isReady: boolean;
  /** Set only in the `error` phase: which step failed. */
  failedStep: ReadinessStep | null;
}

const CORE_STEPS: readonly {
  step: ReadinessStep;
  key: keyof ReadinessInputs;
}[] = [
  { step: "detail", key: "detail" },
  { step: "prefs", key: "prefs" },
  { step: "images", key: "images" },
  { step: "findings", key: "findings" },
  { step: "defects", key: "defects" },
];

/**
 * The pure phase derivation. Exhaustively unit-tested
 * (`analysisReadinessStore.test.ts`) across the query-state × hydration ×
 * activation matrix. Order of precedence:
 *
 *  1. Any core (or data-points) query in error → `error` + the failed step
 *     (first failure in a fixed order wins, so the banner is deterministic).
 *  2. All core queries settled? → at least `coreData`.
 *  3. + filter hydrated AND not applying? → at least `filterApplied`.
 *  4. + an active image (or an empty dataset, which can never have one)? →
 *     `dataPointReady`.
 */
export function deriveReadiness(inputs: ReadinessInputs): ReadinessSnapshot {
  // (1) Error precedence — a failed CORE query is the load-bearing case
  // (never an infinite spinner). The data-points query failure also
  // surfaces (AC4), but ranks AFTER the core steps so a genuine core
  // failure is reported first.
  for (const { step, key } of CORE_STEPS) {
    const sig = inputs[key] as QuerySignal;
    if (sig.isError) {
      return errorSnapshot(step);
    }
  }
  if (inputs.dataPoints.isError) {
    return errorSnapshot("dataPoints");
  }

  // (2) Core data.
  const coreDataReady = CORE_STEPS.every(({ key }) => {
    const sig = inputs[key] as QuerySignal;
    return sig.settled;
  });
  if (!coreDataReady) {
    return {
      phase: "coreData",
      shellReady: true,
      coreDataReady: false,
      filterApplied: false,
      dataPointReady: false,
      isReady: false,
      failedStep: null,
    };
  }

  // (3) Filter applied — hydrated AND the data-points query has settled and
  // is not re-applying. We require the data-points query to have settled
  // too (not just `!filterApplying`) so a momentary pre-fetch idle before
  // the first request fires can't be mistaken for "applied".
  const filterApplied =
    inputs.filterHydrated &&
    !inputs.filterApplying &&
    inputs.dataPoints.settled;
  if (!filterApplied) {
    return {
      phase: "filterApplied",
      shellReady: true,
      coreDataReady: true,
      filterApplied: false,
      dataPointReady: false,
      isReady: false,
      failedStep: null,
    };
  }

  // (4) Data point ready — an active image is set, OR the dataset is empty
  // (no image will ever become active; the surface is otherwise done).
  const dataPointReady = inputs.hasActiveImage || inputs.datasetEmpty;
  return {
    phase: dataPointReady ? "dataPointReady" : "filterApplied",
    shellReady: true,
    coreDataReady: true,
    filterApplied: true,
    dataPointReady,
    isReady: dataPointReady,
    failedStep: null,
  };
}

function errorSnapshot(failedStep: ReadinessStep): ReadinessSnapshot {
  return {
    phase: "error",
    shellReady: true,
    coreDataReady: false,
    filterApplied: false,
    dataPointReady: false,
    isReady: false,
    failedStep,
  };
}

/**
 * The store default — used before any readiness bridge has published a
 * snapshot, and by surfaces that mount a grading panel WITHOUT a bridge
 * (unit tests, direct mounts). It is deliberately OPTIMISTIC
 * (`dataPointReady: true`): the grading gate only ENGAGES once a bridge
 * actively reports the dataset is not yet ready. A surface that wants the
 * gate (the main analysis window AND pop-outs) mounts the bridge, which
 * publishes the real phase synchronously on first render; a standalone
 * panel with no bridge stays un-gated, preserving prior behaviour.
 */
export const INITIAL_SNAPSHOT: ReadinessSnapshot = {
  phase: "dataPointReady",
  shellReady: true,
  coreDataReady: true,
  filterApplied: true,
  dataPointReady: true,
  isReady: true,
  failedStep: null,
};

/** The reset baseline a bridge installs on mount: shell only, nothing settled. */
export const SHELL_SNAPSHOT: ReadinessSnapshot = {
  phase: "shell",
  shellReady: true,
  coreDataReady: false,
  filterApplied: false,
  dataPointReady: false,
  isReady: false,
  failedStep: null,
};

interface AnalysisReadinessState extends ReadinessSnapshot {
  /** Replace the snapshot (the bridge calls this on every input change). */
  set: (snapshot: ReadinessSnapshot) => void;
  /** Reset to the shell default (on unmount / acquisition switch). */
  reset: () => void;
}

export const useAnalysisReadinessStore = create<AnalysisReadinessState>(
  (set) => ({
    ...INITIAL_SNAPSHOT,
    set: (snapshot) => {
      set(snapshot);
    },
    reset: () => {
      // On unmount / acquisition switch, fall back to the optimistic
      // default so a non-analysis surface is never spuriously gated.
      set(INITIAL_SNAPSHOT);
    },
  }),
);

/**
 * E2E test seam (e2e-debt, .vibe/36). The readiness machine reaches
 * `dataPointReady` only once a real image actually activates and the
 * data-points query settles — signals the `page.route`-stubbed Playwright
 * environment can't fully reproduce, so grading actions stay gated and the
 * reclassify/defect-editor specs time out. When the app is running on
 * localhost (the dev / e2e server — NEVER a deployed domain) AND the URL
 * carries `?e2eGradingReady=1`, force the grading gate open. This is purely
 * a UX gate; the backend independently enforces the real grading
 * permissions, so a forced-open gate can never persist an unauthorized
 * mutation. Production (any real hostname) and normal local dev (no param)
 * are unaffected.
 */
function isGradingReadyForced(): boolean {
  if (typeof window === "undefined") return false;
  const { hostname, search } = window.location;
  const isLocal =
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname === "[::1]";
  if (!isLocal) return false;
  return new URLSearchParams(search).has("e2eGradingReady");
}

/**
 * The grading gate (story §"Grading gate (Q5)"). Grading/severity actions
 * are permitted only once `dataPointReady`. Read this BOTH in the disabled
 * prop of grading controls AND at the top of every grading action handler /
 * keyboard listener so the shortcut path no-ops before ready (AC3).
 *
 * Selector-based so consumers re-render when readiness flips; the
 * non-reactive `isGradingReady()` is for use inside event handlers.
 */
export function useGradingReady(): boolean {
  const ready = useAnalysisReadinessStore((s) => s.dataPointReady);
  return ready || isGradingReadyForced();
}

/** Non-reactive read for action handlers / keyboard listeners. */
export function isGradingReady(): boolean {
  return (
    useAnalysisReadinessStore.getState().dataPointReady ||
    isGradingReadyForced()
  );
}

/** The tooltip shown on a grading control disabled by the readiness gate. */
export const GRADING_NOT_READY_TOOLTIP = "Preparing data — one moment";
